import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth";
import { ArcoinMascot, ArcSymbol } from "../Mascot";

const ROLES = [
  { key: "student", label: "Aluno", color: "#00B4D8", icon: "🎒", placeholder: "@primeironome ou seu@email.edu", hint: "Use seu login (@primeironome) e seu RA como senha." },
  { key: "teacher", label: "Professor", color: "#06D6A0", icon: "📚", placeholder: "professor@escola.edu", hint: "Use o e-mail e a senha fornecidos pela coordenação." },
  { key: "admin", label: "Admin", color: "#FF006E", icon: "🏫", placeholder: "admin@escola.edu", hint: "Acesso restrito à administração escolar." },
];

const HOME = { student: "/aluno", teacher: "/professor", admin: "/admin" };

const RoleSelector = ({ value, onChange, disabled }) => (
  <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" role="radiogroup" aria-label="Tipo de acesso">
    {ROLES.map((r) => {
      const active = value === r.key;
      return (
        <motion.button key={r.key} type="button" role="radio" aria-checked={active} disabled={disabled}
          whileHover={{ y: -4 }} whileTap={{ scale: 0.96 }}
          data-testid={`role-select-${r.key}`}
          onClick={() => onChange(r.key)}
          className="arc-card p-4 text-center transition-[box-shadow,transform,background-color]"
          style={{ borderTop: `4px solid ${r.color}`, boxShadow: active ? `0 0 0 3px ${r.color}` : undefined, background: active ? `${r.color}14` : undefined }}>
          <div className="text-3xl mb-1">{r.icon}</div>
          <div className="font-fredoka font-semibold text-sm text-arc-text">{r.label}</div>
          <div className="text-xs mt-1 font-semibold" style={{ color: active ? r.color : undefined }}>{active ? "selecionado" : "selecionar"}</div>
        </motion.button>
      );
    })}
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("student");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const current = ROLES.find((r) => r.key === role);

  const selectRole = (r) => { setRole(r); setError(""); };

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) { setError("Informe seu login e sua senha."); return; }
    setLoading(true);
    try {
      const u = await login(email.trim(), password, role);
      nav(HOME[u.role]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erro ao entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg,#E0F2FE 0%,#F0F8FF 50%,#FFF5D6 100%)" }}>
      <div className="grid md:grid-cols-2 gap-10 max-w-6xl w-full items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-4 mb-6">
            <div className="animate-float"><ArcoinMascot size={110} /></div>
            <div>
              <h1 className="font-fredoka text-5xl md:text-6xl font-bold text-arc-text leading-none">Arcoins</h1>
              <p className="font-nunito text-arc-muted mt-2 text-lg">Banco Escolar Digital</p>
            </div>
          </div>
          <p className="font-nunito text-lg md:text-xl text-arc-text/80 max-w-lg">
            Aprenda a <span className="font-bold text-arc-primary">ganhar</span>, <span className="font-bold text-arc-accent">gastar</span> e <span className="font-bold text-arc-success">poupar</span> <ArcSymbol className="text-2xl" /> Arc praticando no dia a dia da escola.
          </p>
          <p className="mt-8 text-sm font-semibold text-arc-text">Como você quer entrar?</p>
          <RoleSelector value={role} onChange={selectRole} disabled={loading} />
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="arc-card p-8 md:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-3" data-testid="login-selected-role"
            style={{ background: `${current.color}1A`, color: current.color }}>
            {current.icon} Acesso {current.label}
          </div>
          <h2 className="font-fredoka text-3xl font-bold text-arc-text mb-1">Bem-vindo(a) de volta!</h2>
          <p className="text-arc-muted mb-6">Entre para continuar sua jornada financeira.</p>
          <form onSubmit={doLogin} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-semibold text-arc-text mb-2">E-mail ou login</label>
              <input data-testid="login-email-input" type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="arc-input" placeholder={current.placeholder} required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-arc-text mb-2">Senha</label>
              <input data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="arc-input" placeholder="••••••••" required />
            </div>
            <div className="min-h-[44px]">
              {error && <div data-testid="login-error" className="text-arc-accent font-semibold text-sm bg-pink-50 p-3 rounded-2xl">{error}</div>}
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading} className="arc-btn arc-btn-primary w-full disabled:opacity-60">
              {loading ? "Entrando..." : `Entrar como ${current.label} →`}
            </button>
          </form>
          <div className="mt-6 p-4 bg-arc rounded-2xl text-xs text-arc-muted" data-testid="login-role-hint">
            <strong className="text-arc-text">💡 Dica:</strong> {current.hint}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
